import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'

import { assignmentsApi } from '@/services/assignments'
import { jobsApi } from '@/services/jobs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CurrencyInput } from '@/components/common/currency-input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { getErrorMessage } from '@/lib/api'
import { getWorkdayEntryJobBlockedMessage } from '@/lib/job-status'
import { useOrganizationDateTimeFormat } from '@/lib/org-date-locale'
import {
  canEditWorkDayEntrySchedule,
  getWorkDayEntryScheduleEditBlockedMessage,
  getWorkDayEntryStatusBadgeClassName,
  getWorkDayEntryStatusLabel,
} from '@/lib/workday-entry-status'
import {
  buildPlannedStartDateTime,
  buildPlannedStartOptions,
  buildReviewPayloadFromEntry,
  computeAttendanceFromLogged,
  computeHoursFromDateTimeRange,
  DEFAULT_PLANNED_DURATION,
  DEFAULT_PLANNED_START_TIME,
  extractDateFromDateTimeLocal,
  extractTimeFromDateTimeLocal,
  formatAttendancePreview,
  inferPlannedDuration,
  loggedDurationToTotalMinutes,
  orgWallClockToApiDateTime,
  PLANNED_DURATION_OPTIONS,
  resolveAttendanceCheckInWallClock,
  resolveLoggedAttendancePreviewCheckOut,
  resolvePlannedEndFromStart,
  snapTimeToInterval,
  toEntryDateTimeLocalValue,
  toOptionalNumberString,
  toOrgWallClockDateTimeLocal,
  totalMinutesToLoggedDuration,
  validateCheckoutAgainstExistingCheckIn,
  wallClockToInstantMs,
} from '@/lib/workday-entry-schedule'
import { cn } from '@/lib/utils'

const scheduleFormSchema = z
  .object({
    planned_start: z.string().optional(),
    planned_end: z.string().optional(),
    payable_by_client: z.boolean().default(false),
    client_pay_amount: z.string().optional(),
    needs_supervisor_attention: z.boolean().default(false),
    notes: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.planned_start?.trim() && value.planned_end?.trim()) {
      const startDate = new Date(value.planned_start)
      const endDate = new Date(value.planned_end)
      if (
        !Number.isNaN(startDate.getTime())
        && !Number.isNaN(endDate.getTime())
        && startDate > endDate
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Planned start must be on or before the planned end time.',
          path: ['planned_end'],
        })
      }
    }
  })
  .refine(
    (value) => !value.payable_by_client || Boolean(value.client_pay_amount?.trim()),
    {
      message: 'Enter the one-time additional amount payable by the client.',
      path: ['client_pay_amount'],
    },
  )
  .refine(
    (value) => !value.needs_supervisor_attention || Boolean(value.notes?.trim()),
    {
      message: 'Add attention notes when supervisor attention is enabled.',
      path: ['notes'],
    },
  )

type ScheduleFormData = z.infer<typeof scheduleFormSchema>

type WorkDayEntryScheduleDialogProps = {
  entryId: string
  jobId: string
  serviceInstanceId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: () => void | Promise<void>
}

export function WorkDayEntryScheduleDialog({
  entryId,
  jobId,
  serviceInstanceId,
  open,
  onOpenChange,
  onSaved,
}: WorkDayEntryScheduleDialogProps) {
  const labels = useWorkspaceLabels()
  const queryClient = useQueryClient()
  const { locale: organizationDateLocale, timeZone: organizationTimeZoneFromHook } =
    useOrganizationDateTimeFormat(serviceInstanceId)

  const entryQuery = useQuery({
    queryKey: ['workday-entry', entryId],
    queryFn: () => assignmentsApi.getWorkDayEntry(entryId),
    enabled: open && Boolean(entryId),
  })

  const entry = entryQuery.data
  const organizationTimeZone = organizationTimeZoneFromHook
    || (typeof entry?.org_timezone === 'string' ? entry.org_timezone : undefined)
  const [plannedDuration, setPlannedDuration] = useState(DEFAULT_PLANNED_DURATION)
  const [loggedHours, setLoggedHours] = useState('0')
  const [loggedMinutes, setLoggedMinutes] = useState('0')
  const [actualStartDate, setActualStartDate] = useState('')
  const [actualStartTime, setActualStartTime] = useState(DEFAULT_PLANNED_START_TIME)
  const [pendingAttendanceUpdate, setPendingAttendanceUpdate] = useState<{
    checkIn: string
    checkOut: string
    hours: string
  } | null>(null)
  const attendanceBaselineRef = useRef({
    date: '',
    time: '',
    hours: '0',
    minutes: '0',
  })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ScheduleFormData>({
    resolver: zodResolver(scheduleFormSchema),
    defaultValues: {
      planned_start: '',
      planned_end: '',
      payable_by_client: false,
      client_pay_amount: '',
      needs_supervisor_attention: false,
      notes: '',
    },
  })

  const plannedStart = watch('planned_start')
  const plannedEnd = watch('planned_end')
  const payableByClient = watch('payable_by_client')

  const jobQuery = useQuery({
    queryKey: ['job', jobId, serviceInstanceId],
    queryFn: () => jobsApi.getJob(jobId, { service_instance_id: serviceInstanceId ?? undefined }),
    enabled: open && Boolean(jobId),
  })
  const workDayDate = entry?.work_date
    || extractDateFromDateTimeLocal(
      toEntryDateTimeLocalValue(entry?.planned_start, entry?.planned_start_local, organizationTimeZone),
    )
    || ''

  const plannedStartOptions = useMemo(
    () => buildPlannedStartOptions(workDayDate),
    [workDayDate],
  )

  const plannedStartValue = useMemo(() => {
    if (!workDayDate) {
      return ''
    }

    const snappedTime = snapTimeToInterval(extractTimeFromDateTimeLocal(plannedStart))
    return buildPlannedStartDateTime(workDayDate, snappedTime)
  }, [plannedStart, workDayDate])

  const actualStartDateTime = useMemo(() => {
    if (!actualStartDate?.trim() || !actualStartTime?.trim()) {
      return ''
    }

    return buildPlannedStartDateTime(actualStartDate, actualStartTime)
  }, [actualStartDate, actualStartTime])

  const savedAttendanceCheckIn = useMemo(
    () => toEntryDateTimeLocalValue(entry?.check_in, entry?.check_in_local, organizationTimeZone),
    [entry?.check_in, entry?.check_in_local, organizationTimeZone],
  )

  const savedAttendanceCheckOut = useMemo(
    () => toEntryDateTimeLocalValue(entry?.check_out, entry?.check_out_local, organizationTimeZone),
    [entry?.check_out, entry?.check_out_local, organizationTimeZone],
  )

  const loggedAttendancePreview = useMemo(
    () => computeAttendanceFromLogged({
      actualStart: actualStartDateTime,
      loggedHours,
      loggedMinutes,
      timeZone: organizationTimeZone,
    }),
    [actualStartDateTime, loggedHours, loggedMinutes, organizationTimeZone],
  )

  const loggedAttendancePreviewCheckOut = useMemo(
    () => resolveLoggedAttendancePreviewCheckOut({
      computedCheckOut: loggedAttendancePreview.checkOut,
      savedCheckOut: savedAttendanceCheckOut,
      checkOutUtc: entry?.check_out,
      checkOutLocal: entry?.check_out_local,
      timeZone: organizationTimeZone,
    }),
    [
      entry?.check_out,
      entry?.check_out_local,
      loggedAttendancePreview.checkOut,
      organizationTimeZone,
      savedAttendanceCheckOut,
    ],
  )

  const jobBlockedMessage = useMemo(
    () => getWorkdayEntryJobBlockedMessage(jobQuery.data),
    [jobQuery.data],
  )

  const scheduleBlockedMessage = useMemo(() => {
    if (!entry?.status) {
      return null
    }

    if (!canEditWorkDayEntrySchedule(entry.status)) {
      return getWorkDayEntryScheduleEditBlockedMessage(entry.status)
    }

    return null
  }, [entry?.status])

  const isBlocked = Boolean(jobBlockedMessage || scheduleBlockedMessage)

  useEffect(() => {
    if (!open || !entry) {
      return
    }

    reset({
      planned_start: toEntryDateTimeLocalValue(entry.planned_start, entry.planned_start_local, organizationTimeZone),
      planned_end: toEntryDateTimeLocalValue(entry.planned_end, entry.planned_end_local, organizationTimeZone),
      payable_by_client: Boolean(entry.payable_by_client),
      client_pay_amount: toOptionalNumberString(entry.client_pay_amount),
      needs_supervisor_attention: Boolean(entry.needs_supervisor_attention),
      notes: entry.notes || '',
    })

    setPlannedDuration(
      inferPlannedDuration(
        toEntryDateTimeLocalValue(entry.planned_start, entry.planned_start_local, organizationTimeZone),
        toEntryDateTimeLocalValue(entry.planned_end, entry.planned_end_local, organizationTimeZone),
      ),
    )

    const checkIn = toEntryDateTimeLocalValue(entry.check_in, entry.check_in_local, organizationTimeZone)
    const checkOut = toEntryDateTimeLocalValue(entry.check_out, entry.check_out_local, organizationTimeZone)
    const plannedStartValue = toEntryDateTimeLocalValue(entry.planned_start, entry.planned_start_local, organizationTimeZone)
    const workDate = entry.work_date || extractDateFromDateTimeLocal(plannedStartValue)
    const defaultTime = extractTimeFromDateTimeLocal(checkIn || plannedStartValue) || DEFAULT_PLANNED_START_TIME

    const nextActualStartDate = checkIn
      ? (extractDateFromDateTimeLocal(checkIn) || workDate)
      : workDate
    const nextActualStartTime = checkIn
      ? (extractTimeFromDateTimeLocal(checkIn) || DEFAULT_PLANNED_START_TIME)
      : defaultTime

    setActualStartDate(nextActualStartDate)
    setActualStartTime(nextActualStartTime)
    setPendingAttendanceUpdate(null)

    let nextLoggedHours = '0'
    let nextLoggedMinutes = '0'

    if (checkIn && checkOut) {
      const startMs = wallClockToInstantMs(checkIn, organizationTimeZone)
      const endMs = wallClockToInstantMs(checkOut, organizationTimeZone)
      if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
        const diffMinutes = Math.round((endMs - startMs) / 60_000)
        const duration = totalMinutesToLoggedDuration(diffMinutes)
        nextLoggedHours = duration.hours
        nextLoggedMinutes = duration.minutes
      }
    } else if (checkIn) {
      nextLoggedHours = '0'
      nextLoggedMinutes = '0'
    }

    setLoggedHours(nextLoggedHours)
    setLoggedMinutes(nextLoggedMinutes)
    attendanceBaselineRef.current = {
      date: nextActualStartDate,
      time: nextActualStartTime,
      hours: nextLoggedHours,
      minutes: nextLoggedMinutes,
    }
  }, [entry, open, organizationTimeZone, reset])

  useEffect(() => {
    if (!plannedStart?.trim()) {
      return
    }

    const endDate = resolvePlannedEndFromStart(plannedStart, plannedDuration)
    if (endDate) {
      setValue('planned_end', endDate, { shouldValidate: true })
    }
  }, [plannedDuration, plannedStart, setValue])

  const handleApplyCheckoutNow = () => {
    const checkIn = actualStartDateTime
      || resolveAttendanceCheckInWallClock({
        checkInUtc: entry?.check_in,
        checkInLocal: entry?.check_in_local,
        timeZone: organizationTimeZone,
        fallback: actualStartDateTime,
      })
    const checkOut = toOrgWallClockDateTimeLocal(new Date(), organizationTimeZone)

    if (!checkIn.trim()) {
      toast.error('Set a check-in time before recording check-out.')
      return
    }

    const validationError = validateCheckoutAgainstExistingCheckIn({
      checkInWallClock: checkIn,
      checkInUtc: entry?.check_in,
      checkOutWallClock: checkOut,
      timeZone: organizationTimeZone,
      assumeExistingCheckIn: entry?.status === 'in_progress' || entry?.status === 'logged',
    })
    if (validationError) {
      toast.error(validationError)
      return
    }

    const startMs = wallClockToInstantMs(checkIn, organizationTimeZone)
    const endMs = wallClockToInstantMs(checkOut, organizationTimeZone)
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs >= startMs) {
      const duration = totalMinutesToLoggedDuration(Math.round((endMs - startMs) / 60_000))
      setLoggedHours(duration.hours)
      setLoggedMinutes(duration.minutes)
    }

    setPendingAttendanceUpdate({
      checkIn,
      checkOut,
      hours: computeHoursFromDateTimeRange(checkIn, checkOut, organizationTimeZone),
    })
  }

  const saveMutation = useMutation({
    mutationFn: async (data: ScheduleFormData) => {
      if (!entry) {
        throw new Error('Work day entry not loaded.')
      }

      const submitWorkDate = workDayDate || ''
      const plannedStartForSubmit = submitWorkDate
        ? buildPlannedStartDateTime(
            submitWorkDate,
            snapTimeToInterval(extractTimeFromDateTimeLocal(data.planned_start)),
          )
        : data.planned_start?.trim() || ''
      const plannedEndForSubmit = resolvePlannedEndFromStart(
        plannedStartForSubmit,
        plannedDuration,
      )

      const existingCheckIn = toEntryDateTimeLocalValue(entry.check_in, entry.check_in_local, organizationTimeZone)
      const existingCheckOut = toEntryDateTimeLocalValue(entry.check_out, entry.check_out_local, organizationTimeZone)
      const existingHours = toOptionalNumberString(entry.hours) || '0.00'

      let checkInForSubmit = existingCheckIn
      let checkOutForSubmit = existingCheckOut
      let hoursForSubmit = existingHours

      const baseline = attendanceBaselineRef.current
      const attendanceChanged = baseline.date !== actualStartDate
        || baseline.time !== actualStartTime
        || String(Number(baseline.hours) || 0) !== String(Number(loggedHours) || 0)
        || String(Number(baseline.minutes) || 0) !== String(Number(loggedMinutes) || 0)

      if (pendingAttendanceUpdate) {
        checkInForSubmit = pendingAttendanceUpdate.checkIn
        checkOutForSubmit = pendingAttendanceUpdate.checkOut
        hoursForSubmit = pendingAttendanceUpdate.hours
      } else if (attendanceChanged) {
        const computed = computeAttendanceFromLogged({
          actualStart: actualStartDateTime,
          loggedHours,
          loggedMinutes,
          timeZone: organizationTimeZone,
        })
        checkInForSubmit = computed.checkIn
        checkOutForSubmit = computed.checkOut
        hoursForSubmit = computed.hours
      }

      return assignmentsApi.updateWorkDayEntry(entryId, {
        job: entry.job,
        work_day: entry.work_day,
        worker: entry.worker,
        worker_skill: entry.worker_skill || undefined,
        planned_start: orgWallClockToApiDateTime(plannedStartForSubmit, organizationTimeZone),
        planned_end: orgWallClockToApiDateTime(plannedEndForSubmit, organizationTimeZone),
        check_in: orgWallClockToApiDateTime(checkInForSubmit, organizationTimeZone),
        check_out: orgWallClockToApiDateTime(checkOutForSubmit, organizationTimeZone),
        hours: hoursForSubmit,
        pay_hourly_rate: entry.pay_hourly_rate ?? undefined,
        pay_flat: entry.pay_flat ?? undefined,
        night_pay: entry.night_pay ?? undefined,
        holiday_pay: entry.holiday_pay ?? undefined,
        peak_pay: entry.peak_pay ?? undefined,
        special_pay: entry.special_pay ?? undefined,
        payable_by_client: data.payable_by_client,
        client_pay_amount: data.payable_by_client ? (data.client_pay_amount?.trim() || undefined) : undefined,
        needs_supervisor_attention: Boolean(data.needs_supervisor_attention),
        notes: data.notes?.trim() || undefined,
        ...buildReviewPayloadFromEntry(entry),
      })
    },
    onSuccess: async () => {
      toast.success('Entry schedule updated successfully')
      await queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
      await queryClient.invalidateQueries({ queryKey: ['workdays'] })
      await queryClient.invalidateQueries({ queryKey: ['workday-entry', entryId] })
      await queryClient.invalidateQueries({ queryKey: ['workday-entry-audits', entryId] })
      await queryClient.invalidateQueries({ queryKey: ['job-work-day-entries'] })
      await queryClient.invalidateQueries({ queryKey: ['worker-work-day-entries'] })
      await queryClient.invalidateQueries({ queryKey: ['assignment-workday-entries'] })
      await queryClient.invalidateQueries({ queryKey: ['attention-work-day-entries'] })
      await onSaved?.()
      onOpenChange(false)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const onSubmit = (data: ScheduleFormData) => {
    saveMutation.mutate(data)
  }

  const statusLabel = entry?.status_label || getWorkDayEntryStatusLabel(entry?.status)
  const needsReview = watch('needs_supervisor_attention')
  const workDayHeading = workDayDate
    ? new Intl.DateTimeFormat(organizationDateLocale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(Date.UTC(
        Number(workDayDate.slice(0, 4)),
        Number(workDayDate.slice(5, 7)) - 1,
        Number(workDayDate.slice(8, 10)),
      )))
    : ''
  const contextLine = [entry?.worker_name, entry?.job_title || (entry ? `Job #${entry.job}` : ''), workDayHeading]
    .filter(Boolean)
    .join(' · ')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-row-action="true"
        className="z-[130] flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <DialogHeader className="border-b border-border/60 px-6 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <DialogTitle>Edit {labels.workDayEntry}</DialogTitle>
            {entry?.status || entry?.status_label ? (
              <Badge
                variant="outline"
                className={cn('font-medium', getWorkDayEntryStatusBadgeClassName(entry.status))}
              >
                {statusLabel}
              </Badge>
            ) : null}
          </div>
          {contextLine ? <DialogDescription>{contextLine}</DialogDescription> : null}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {entryQuery.isLoading || jobQuery.isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <Spinner className="mr-2" />
              <span className="text-sm text-muted-foreground">Loading...</span>
            </div>
          ) : entryQuery.isError || !entry ? (
            <p className="text-sm text-destructive">Could not load this shift.</p>
          ) : isBlocked ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {scheduleBlockedMessage || jobBlockedMessage}
            </p>
          ) : (
            <form id="workday-schedule-dialog-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <input type="hidden" {...register('planned_start')} />
              <input type="hidden" {...register('planned_end')} />

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="schedule_dialog_planned_start">Planned start</Label>
                  <Select
                    value={plannedStartValue || undefined}
                    onValueChange={(value) => {
                      setValue('planned_start', value, { shouldDirty: true, shouldValidate: true })
                    }}
                    disabled={!workDayDate || plannedStartOptions.length === 0}
                  >
                    <SelectTrigger id="schedule_dialog_planned_start" aria-invalid={!!errors.planned_start}>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent className="z-[140]">
                      {plannedStartOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.planned_start ? (
                    <p className="text-sm text-destructive">{errors.planned_start.message}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="schedule_dialog_planned_duration">Duration</Label>
                  <Select value={plannedDuration} onValueChange={setPlannedDuration}>
                    <SelectTrigger id="schedule_dialog_planned_duration" className="w-full">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent className="z-[140]">
                      <SelectGroup>
                        <SelectLabel>Minutes</SelectLabel>
                        {PLANNED_DURATION_OPTIONS.filter((option) => option.group === 'minutes').map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>Hours</SelectLabel>
                        {PLANNED_DURATION_OPTIONS.filter((option) => option.group === 'hours').map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Ends</Label>
                  <p className="flex h-9 items-center text-sm font-medium text-foreground">
                    {plannedEnd
                      ? formatAttendancePreview(plannedEnd, organizationDateLocale, organizationTimeZone)
                      : '—'}
                  </p>
                  {errors.planned_end ? (
                    <p className="text-sm text-destructive">{errors.planned_end.message}</p>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-4 border-t border-border/60 pt-4 sm:grid-cols-4">
                {actualStartDate && workDayDate && actualStartDate !== workDayDate ? (
                  <div className="space-y-2 sm:col-span-4">
                    <Label htmlFor="schedule_dialog_actual_start_date">Check-in date</Label>
                    <Input
                      id="schedule_dialog_actual_start_date"
                      type="date"
                      value={actualStartDate}
                      onChange={(event) => {
                        setPendingAttendanceUpdate(null)
                        setActualStartDate(event.target.value)
                      }}
                    />
                  </div>
                ) : null}
                <div className="space-y-2">
                  <Label htmlFor="schedule_dialog_actual_start_time">Check-in</Label>
                  <Input
                    id="schedule_dialog_actual_start_time"
                    type="time"
                    value={actualStartTime}
                    onChange={(event) => {
                      setPendingAttendanceUpdate(null)
                      setActualStartTime(event.target.value)
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="schedule_dialog_logged_hours">Hours</Label>
                  <Input
                    id="schedule_dialog_logged_hours"
                    type="number"
                    min={0}
                    max={24}
                    step={1}
                    value={loggedHours}
                    onChange={(event) => {
                      setPendingAttendanceUpdate(null)
                      setLoggedHours(String(Math.max(0, Number(event.target.value) || 0)))
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="schedule_dialog_logged_minutes">Minutes</Label>
                  <Input
                    id="schedule_dialog_logged_minutes"
                    type="number"
                    min={0}
                    max={59}
                    step={1}
                    value={loggedMinutes}
                    onChange={(event) => {
                      setPendingAttendanceUpdate(null)
                      setLoggedMinutes(String(Math.max(0, Math.min(59, Number(event.target.value) || 0))))
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Check-out</Label>
                  <div className="flex h-9 items-center gap-2">
                    <p className="text-sm font-medium text-foreground">
                      {pendingAttendanceUpdate?.checkOut
                        ? formatAttendancePreview(pendingAttendanceUpdate.checkOut, organizationDateLocale, organizationTimeZone)
                        : loggedDurationToTotalMinutes(loggedHours, loggedMinutes) > 0 && loggedAttendancePreviewCheckOut
                          ? formatAttendancePreview(loggedAttendancePreviewCheckOut, organizationDateLocale, organizationTimeZone)
                          : '—'}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                      onClick={handleApplyCheckoutNow}
                      disabled={!actualStartDateTime.trim() && !savedAttendanceCheckIn}
                    >
                      Now
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-4">
                <div className="flex items-center gap-3">
                  <Label htmlFor="schedule_dialog_needs_review" className="text-sm font-medium">Needs review</Label>
                  <Switch
                    id="schedule_dialog_needs_review"
                    checked={needsReview}
                    onCheckedChange={(checked) => {
                      setValue('needs_supervisor_attention', checked, { shouldDirty: true, shouldValidate: true })
                    }}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Label htmlFor="schedule_dialog_payable" className="text-sm font-medium">Client payable</Label>
                  <Switch
                    id="schedule_dialog_payable"
                    checked={payableByClient}
                    onCheckedChange={(checked) => {
                      setValue('payable_by_client', checked, { shouldDirty: true, shouldValidate: true })
                      if (!checked) {
                        setValue('client_pay_amount', '', { shouldDirty: true, shouldValidate: true })
                      }
                    }}
                  />
                </div>
              </div>

              {needsReview ? (
                <div className="space-y-2">
                  <Label htmlFor="schedule_dialog_attention_notes">Note</Label>
                  <Textarea
                    id="schedule_dialog_attention_notes"
                    rows={2}
                    {...register('notes')}
                  />
                  {errors.notes ? <p className="text-sm text-destructive">{errors.notes.message}</p> : null}
                </div>
              ) : null}

              {payableByClient ? (
                <div className="max-w-xs space-y-2">
                  <Label htmlFor="schedule_dialog_client_pay_amount">Client amount</Label>
                  <CurrencyInput id="schedule_dialog_client_pay_amount" {...register('client_pay_amount')} />
                  {errors.client_pay_amount ? (
                    <p className="text-sm text-destructive">{errors.client_pay_amount.message}</p>
                  ) : null}
                </div>
              ) : null}
            </form>
          )}
        </div>

        <DialogFooter className="border-t border-border/60 px-6 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="workday-schedule-dialog-form"
            disabled={isBlocked || saveMutation.isPending || entryQuery.isLoading}
          >
            {saveMutation.isPending ? <Spinner className="mr-2" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
